// Developer Platform — Asana work-management integration card (worksync V1).
// Connect (OAuth) → pick workspace → map lifecycle packages to Asana projects →
// delegate manual tasks → health. Tokens never reach this code: the backend
// returns only status booleans and the consent URL.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Loader2, RefreshCcw, Unplug, Workflow } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  disconnectAsana, getAsanaStatus, listAsanaWorkspaces, startAsanaOAuth, updateAsanaConfig,
  type AsanaConfig, type AsanaStatus, type AsanaWorkspace,
} from '@/services/developerService';
import { getLifecyclePackages, type LifecyclePackage } from '@/services/lifecycleService';
import { AsanaAssignmentsSection } from './AsanaAssignmentsSection';
import { errMsg, fmtDateTime, inputCls } from './common';

const WEBHOOK_BADGE: Record<string, string> = {
  active: 'bg-success-soft text-success-strong border-transparent',
  pending: 'bg-muted text-muted-foreground border-border',
  error: 'bg-danger-soft text-danger-strong border-transparent',
  disabled: 'bg-muted text-muted-foreground border-border',
};

// ------------------------------------------------------------- configure dialog
const ConfigureDialog: React.FC<{
  status: AsanaStatus;
  onSaved: (s: AsanaStatus) => void;
  onClose: () => void;
}> = ({ status, onSaved, onClose }) => {
  const { t } = useTranslation('admin');
  const [packages, setPackages] = useState<LifecyclePackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const cfg = status.config || {};
  const [projects, setProjects] = useState<Record<string, Record<string, string>>>(
    () => JSON.parse(JSON.stringify(cfg.projects || {})),
  );
  const [delegation, setDelegation] = useState<Record<string, Record<string, string[]>>>(
    () => JSON.parse(JSON.stringify(cfg.delegation || {})),
  );
  const [inbound, setInbound] = useState<boolean>(!!cfg.inbound_enabled);
  const [outbound, setOutbound] = useState<boolean>(cfg.outbound_enabled !== false);

  useEffect(() => {
    let alive = true;
    getLifecyclePackages()
      .then((r) => { if (alive) setPackages(r.packages || []); })
      .catch(() => { if (alive) setPackages([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const setProject = (type: string, pkg: string, gid: string) => {
    setProjects((cur) => ({ ...cur, [type]: { ...(cur[type] || {}), [pkg]: gid.trim() } }));
  };
  const toggleTask = (type: string, pkg: string, key: string) => {
    setDelegation((cur) => {
      const forType = { ...(cur[type] || {}) };
      const list = new Set(forType[pkg] || []);
      if (list.has(key)) list.delete(key); else list.add(key);
      forType[pkg] = Array.from(list);
      return { ...cur, [type]: forType };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Drop empty project mappings so unmapped packages simply do not sync.
      const cleanProjects: Record<string, Record<string, string>> = {};
      for (const [type, byPkg] of Object.entries(projects)) {
        const kept = Object.fromEntries(Object.entries(byPkg).filter(([, gid]) => gid));
        if (Object.keys(kept).length) cleanProjects[type] = kept;
      }
      const patch: AsanaConfig = {
        projects: cleanProjects,
        delegation,
        inbound_enabled: inbound,
        outbound_enabled: outbound,
      };
      await updateAsanaConfig(patch);
      const fresh = await getAsanaStatus();
      onSaved(fresh);
      toast.success(t('developer.asana_cfg_saved', { defaultValue: 'Asana sync configuration saved.' }));
      onClose();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.asana_cfg_failed', { defaultValue: 'Could not save the configuration.' }))));
    } finally {
      setSaving(false);
    }
  };

  const section = (type: 'onboarding' | 'offboarding') => {
    const pkgs = packages.filter((p) => p.type === type);
    if (pkgs.length === 0) return null;
    return (
      <div key={type} className="space-y-3">
        <h4 className="text-sm font-semibold capitalize">{type}</h4>
        {pkgs.map((p) => {
          const gid = projects[type]?.[p.name] || '';
          const delegated = new Set(delegation[type]?.[p.name] || []);
          const manualTasks = p.tasks.filter((task) => task.verify === 'manual');
          return (
            <div key={p.name} className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{p.name}</span>
                <input
                  className={`${inputCls} h-8 text-xs flex-1 min-w-[220px]`}
                  placeholder={t('developer.asana_project_ph', { defaultValue: 'Asana project GID (from the project URL)' })}
                  value={gid}
                  onChange={(e) => setProject(type, p.name, e.target.value)}
                />
              </div>
              {gid && manualTasks.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {t('developer.asana_delegate_hint', { defaultValue: 'Tick the tasks that should be executed in Asana. Everything else stays in KaziPeople.' })}
                  </p>
                  {manualTasks.map((task) => (
                    <label key={task.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={delegated.has(task.key)}
                        onChange={() => toggleTask(type, p.name, task.key)}
                      />
                      <span>{task.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {delegated.has(task.key)
                          ? t('developer.asana_in_asana', { defaultValue: 'Execute in Asana' })
                          : t('developer.asana_in_kp', { defaultValue: 'Execute in KaziPeople' })}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {gid && manualTasks.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('developer.asana_no_manual', { defaultValue: 'This package has no manual tasks; automatically verified tasks always stay in KaziPeople.' })}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('developer.asana_cfg_title', { defaultValue: 'Configure Asana Sync' })}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {t('developer.asana_loading_pkgs', { defaultValue: 'Loading lifecycle packages…' })}
          </div>
        ) : (
          <div className="space-y-5">
            {section('onboarding')}
            {section('offboarding')}
            <div className="rounded-lg border border-border p-3 space-y-2">
              <h4 className="text-sm font-semibold">{t('developer.asana_sync_dir', { defaultValue: 'Synchronization' })}</h4>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={outbound} onChange={(e) => setOutbound(e.target.checked)} />
                {t('developer.asana_outbound', { defaultValue: 'KaziPeople → Asana: create delegated tasks when onboarding or offboarding starts' })}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={inbound} onChange={(e) => setInbound(e.target.checked)} />
                {t('developer.asana_inbound', { defaultValue: 'Asana → KaziPeople: completing a task in Asana completes it here (via webhook)' })}
              </label>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={save} disabled={saving || loading}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {t('save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------- card
export const AsanaCard: React.FC<{ available: boolean }> = ({ available }) => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const [status, setStatus] = useState<AsanaStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [workspaces, setWorkspaces] = useState<AsanaWorkspace[] | null>(null);
  const [pickedWs, setPickedWs] = useState('');
  const [configuring, setConfiguring] = useState(false);

  const refresh = useCallback(() => {
    getAsanaStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    if (available) refresh();
  }, [available, refresh]);

  // Landing from the OAuth redirect (?asana=connected|denied).
  useEffect(() => {
    const params = new URLSearchParams(search);
    const result = params.get('asana');
    if (!result) return;
    if (result === 'connected') {
      toast.success(t('developer.asana_connected_toast', { defaultValue: 'Asana connected. Now pick a workspace.' }));
      refresh();
    } else if (result === 'denied') {
      toast.error(t('developer.asana_denied_toast', { defaultValue: 'Asana authorization was cancelled.' }));
    }
    params.delete('asana');
    navigate({ pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
  }, [search, pathname, navigate, refresh, t]);

  const connect = async () => {
    setBusy(true);
    try {
      window.location.href = await startAsanaOAuth();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.asana_connect_failed', { defaultValue: 'Could not start the Asana connection.' }))));
      setBusy(false);
    }
  };

  const loadWorkspaces = async () => {
    setBusy(true);
    try {
      const ws = await listAsanaWorkspaces();
      setWorkspaces(ws);
      if (ws.length === 1) setPickedWs(ws[0].gid);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.asana_ws_failed', { defaultValue: 'Could not load Asana workspaces.' }))));
    } finally {
      setBusy(false);
    }
  };

  const saveWorkspace = async () => {
    if (!pickedWs) return;
    setBusy(true);
    try {
      await updateAsanaConfig({ workspace_gid: pickedWs });
      setWorkspaces(null);
      refresh();
      toast.success(t('developer.asana_ws_saved', { defaultValue: 'Workspace selected.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.asana_ws_save_failed', { defaultValue: 'Could not save the workspace.' }))));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    const ok = await confirm({
      title: t('developer.asana_disc_title', { defaultValue: 'Disconnect Asana' }),
      message: t('developer.asana_disc_msg', {
        defaultValue: 'Disconnect Asana? Task sync stops and the stored authorization is revoked. Existing Asana tasks are not deleted.',
      }),
      confirmLabel: t('developer.disconnect', { defaultValue: 'Disconnect' }),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await disconnectAsana();
      refresh();
      toast.success(t('developer.asana_disc_ok', { defaultValue: 'Asana disconnected.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.asana_disc_failed', { defaultValue: 'Could not disconnect Asana.' }))));
    } finally {
      setBusy(false);
    }
  };

  const webhookSummary = useMemo(() => {
    const hooks = status?.webhooks || [];
    if (!status?.config?.inbound_enabled || hooks.length === 0) return null;
    const bad = hooks.filter((h) => h.status !== 'active');
    return bad.length === 0
      ? { label: t('developer.asana_wh_healthy', { defaultValue: 'Webhook healthy' }), cls: WEBHOOK_BADGE.active }
      : { label: t('developer.asana_wh_issue', { defaultValue: '{{n}} webhook issue(s)', n: bad.length }), cls: WEBHOOK_BADGE.error };
  }, [status, t]);

  if (!available) return null;

  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Workflow className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Asana</span>
            {status?.connected ? (
              <Badge className="bg-success-soft text-success-strong border-transparent">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {t('developer.asana_connected', { defaultValue: 'Connected' })}
              </Badge>
            ) : (
              <Badge className="bg-muted text-muted-foreground border-border">
                {t('developer.asana_not_connected', { defaultValue: 'Not connected' })}
              </Badge>
            )}
            {status?.needs_reauthorization && (
              <Badge className="bg-danger-soft text-danger-strong border-transparent">
                {t('developer.asana_reauth', { defaultValue: 'Needs reauthorization' })}
              </Badge>
            )}
            {webhookSummary && <Badge className={webhookSummary.cls}>{webhookSummary.label}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {!status?.connected || status.needs_reauthorization ? (
              <Button size="sm" onClick={connect} disabled={busy}>
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                {status?.needs_reauthorization
                  ? t('developer.asana_reconnect', { defaultValue: 'Reconnect Asana' })
                  : t('developer.asana_connect', { defaultValue: 'Connect Asana' })}
              </Button>
            ) : (
              <>
                {status.workspace_gid && (
                  <Button size="sm" variant="outline" onClick={() => setConfiguring(true)} disabled={busy}>
                    {t('developer.asana_configure', { defaultValue: 'Configure sync' })}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={refresh} disabled={busy} title={t('refresh', { defaultValue: 'Refresh' })}>
                  <RefreshCcw className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={disconnect} disabled={busy}>
                  <Unplug className="w-4 h-4 mr-1" />
                  {t('developer.disconnect', { defaultValue: 'Disconnect' })}
                </Button>
              </>
            )}
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          {t('developer.asana_desc', {
            defaultValue: 'Run onboarding and offboarding work in Asana: KaziPeople creates the delegated tasks, and completing them in Asana updates the checklist here automatically.',
          })}
        </p>

        {status?.connected && !status.workspace_gid && (
          <div className="rounded-lg border border-border p-3 space-y-2">
            <p className="text-sm font-medium">
              {t('developer.asana_pick_ws', { defaultValue: 'Select the Asana workspace to use' })}
            </p>
            {workspaces === null ? (
              <Button size="sm" variant="outline" onClick={loadWorkspaces} disabled={busy}>
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                {t('developer.asana_load_ws', { defaultValue: 'Load workspaces' })}
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <select className={`${inputCls} h-8 text-sm w-[260px]`} value={pickedWs} onChange={(e) => setPickedWs(e.target.value)}>
                  <option value="">{t('developer.asana_ws_choose', { defaultValue: 'Choose a workspace…' })}</option>
                  {workspaces.map((w) => <option key={w.gid} value={w.gid}>{w.name}</option>)}
                </select>
                <Button size="sm" onClick={saveWorkspace} disabled={!pickedWs || busy}>
                  {t('save', { defaultValue: 'Save' })}
                </Button>
              </div>
            )}
          </div>
        )}

        {status?.connected && status.workspace_gid && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-muted-foreground">
            <span>
              {t('developer.asana_ws', { defaultValue: 'Workspace' })}:{' '}
              <span className="text-foreground font-medium">{status.workspace_name || status.workspace_gid}</span>
            </span>
            <span>
              {t('developer.asana_last_sync', { defaultValue: 'Last sync' })}:{' '}
              {status.last_sync_at ? fmtDateTime(status.last_sync_at) : t('developer.asana_never', { defaultValue: 'never' })}
            </span>
            {status.last_error && !status.needs_reauthorization && (
              <span className="text-danger-strong">{status.last_error}</span>
            )}
            {Object.entries(status.config?.project_urls || {}).slice(0, 1).map(([gid, url]) => (
              <a key={gid} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-orange-600 dark:text-orange-400 hover:underline">
                {t('developer.asana_open', { defaultValue: 'Open in Asana' })} <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        )}

        {status?.connected && status.workspace_gid && <AsanaAssignmentsSection />}

        {configuring && status && (
          <ConfigureDialog status={status} onSaved={setStatus} onClose={() => setConfiguring(false)} />
        )}
      </CardContent>
    </Card>
  );
};
